// components/landing/sections/RotatingSentences.tsx
// ---------------------------------------------------------------------------
// Shared component: rotating sentences that showcase Athena's capabilities.
// Used in both the landing hero card and login page.
//
// Sentences are persona-driven, mixing NL query examples (italic, in quotes)
// with feature highlights. They rotate every 5 seconds with a smooth fade.
//
// Tone: scientific, warm, observational (David Attenborough narration style).
// Not salesy. Doctors appreciate clarity and substance over marketing speak.
// ---------------------------------------------------------------------------

import { alpha, Box, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { athenaColors } from "../colors";

// ---------------------------------------------------------------------------
// Sentence bank
// ---------------------------------------------------------------------------
interface Sentence {
  text: string;
  isQuery: boolean;
}

export const SENTENCES: Sentence[] = [
  // NL query examples
  {
    text: '"Show me patients diagnosed with metastasis within three months of their primary diagnosis."',
    isQuery: true,
  },
  {
    text: "A single pathology report holds layers of information. Athena reads it, structures it, and organizes every detail.",
    isQuery: false,
  },
  {
    text: '"Which BRAF V600E-positive melanoma patients showed complete response to immunotherapy?"',
    isQuery: true,
  },
  {
    text: "Across hospitals, data speaks different languages. Here, it all converges into one unified structure.",
    isQuery: false,
  },
  {
    text: '"Compare overall survival across lung cancer patients with EGFR mutations versus wild-type."',
    isQuery: true,
  },
  {
    text: "Forty-four cancer types, each with its own complexity. One platform, built to understand them all.",
    isQuery: false,
  },
  {
    text: "Your patient population, viewed as a living cohort, revealing patterns as they emerge.",
    isQuery: false,
  },
  {
    text: '"List all patients with PD-L1 expression above 50% who have not yet started treatment."',
    isQuery: true,
  },
  {
    text: "Unstructured clinical notes become structured, queryable datasets, ready to download and analyze.",
    isQuery: false,
  },
  {
    text: "Pseudonymized cohorts shared securely with collaborators, in the time it takes to send an email.",
    isQuery: false,
  },
  {
    text: '"What is the median time to progression for stage III colorectal patients on FOLFOX?"',
    isQuery: true,
  },
  {
    text: "A clinician speaks. Athena listens, transcribes, and structures the data within minutes.",
    isQuery: false,
  },
  {
    text: "In a multi-center study, each site uploads its own format. The platform harmonizes everything seamlessly.",
    isQuery: false,
  },
  {
    text: "Data structuring that once required weeks of manual effort now happens in moments.",
    isQuery: false,
  },
  {
    text: '"Show me the KRAS mutation distribution across our entire gastrointestinal cancer cohort."',
    isQuery: true,
  },
  {
    text: "Query your data in natural language, as you would ask a colleague. The answers arrive instantly.",
    isQuery: false,
  },
  {
    text: "Mutation prevalence, treatment responses, biomarker trends, all observed from a single vantage point.",
    isQuery: false,
  },
  {
    text: "Every extraction, every query, every export is recorded. A complete audit trail, by design.",
    isQuery: false,
  },
  {
    text: "Built within European Azure instances, compliant with GDPR and HIPAA, secured under a BAA with Microsoft.",
    isQuery: false,
  },
  {
    text: "From a single patient record to population-wide analytics. The scope adjusts to your question.",
    isQuery: false,
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface RotatingSentencesProps {
  /** "dark" uses periwinkle/white palette for the hero right panel dark background */
  variant?: "default" | "dark";
}

export default function RotatingSentences({ variant = "default" }: RotatingSentencesProps) {
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const isDark = variant === "dark";

  // In dark variant, mirror the navbar's link color: alpha(secondary.main, 0.85) = periwinkle
  const accentColor = isDark ? theme.palette.secondary.main : theme.palette.primary.main;
  // Query text (monospace italic) — white like navbar's primary elements
  const textColor = isDark ? theme.palette.common.white : theme.palette.text.secondary;
  // Prose narrative — periwinkle at the same opacity as navbar nav links
  const narrativeColor = isDark ? alpha(theme.palette.secondary.main, 0.85) : alpha(theme.palette.text.secondary, 0.9);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % SENTENCES.length);
        setVisible(true);
      }, 1000);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const sentence = SENTENCES[index];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: sentence.isQuery ? "left" : "center",
        minHeight: 140,
        px: 2,
        width: "100%",
      }}
    >
      {sentence.isQuery ? (
        /* Query style — looks like a real query input */
        <Box
          sx={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(-6px)",
            transition: "opacity 1s ease, transform 1s ease",
            width: "100%",
            maxWidth: 360,
            borderLeft: `3px solid ${alpha(accentColor, 0.5)}`,
            pl: 1.5,
            py: 0.5,
            bgcolor: alpha(accentColor, isDark ? 0.08 : 0.04),
            borderRadius: "0 6px 6px 0",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              letterSpacing: "0.04em",
              color: alpha(accentColor, isDark ? 0.9 : 0.7),
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            Natural Language Query
          </Typography>
          <Typography
            sx={{
              fontFamily: "monospace",
              fontSize: "0.92rem",
              fontStyle: "italic",
              fontWeight: 500,
              lineHeight: 1.6,
              color: textColor,
            }}
          >
            {sentence.text}
          </Typography>
        </Box>
      ) : (
        /* Narrative style — clean, centred prose */
        <Typography
          sx={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(-8px)",
            transition: "opacity 1s ease, transform 1s ease",
            fontSize: "1.05rem",
            fontWeight: 400,
            lineHeight: 1.75,
            color: narrativeColor,
            maxWidth: 340,
            fontStyle: "normal",
          }}
        >
          {sentence.text}
        </Typography>
      )}
    </Box>
  );
}
